import { Button, Skeleton, Text } from '$components'
import { cn } from '$components'
import { useInfiniteQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { appointmentSlotsInfiniteOptions } from '$api-client/@tanstack/react-query.gen'
import { AppointmentSlot } from '$api-client/types.gen'

type AvailableSlotsProps = {
  duration: number
  date: string
  personIds?: number[]
  enabled?: boolean
  selectedSlotId?: number
  onSlotSelect: (slot: AppointmentSlot & { id: number }) => void
  emptyStateMessage?: string
  loadingStateMessage?: string
}

export function AvailableSlots({
  duration,
  date,
  personIds = [],
  enabled = true,
  selectedSlotId,
  onSlotSelect,
  emptyStateMessage = 'No slots available',
}: AvailableSlotsProps) {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...appointmentSlotsInfiniteOptions({
      query: {
        duration,
        date,
        groomers: personIds,
      },
    }),
    getNextPageParam: (lastPage) => lastPage.links.next ? lastPage.meta.current_page + 1 : null,
    initialPageParam: 1,
    enabled,
    placeholderData: (prev) => prev,
  })

  const slots = useMemo(() => {
    return data?.pages.flatMap(page => page.data.map((slot, index) => ({ id: index + 1, ...slot })))
  }, [data])

  if (isLoading) {
    return (
      <Skeleton
        height={64}
        className="rounded-control"
      />
    )
  }

  if (!slots || slots.length === 0) {
    return (
      <div className="p-stack-section bg-surface-muted rounded-surface flex items-center justify-center">
        <Text
          type="s"
          weight="light"
        >
          {emptyStateMessage}
        </Text>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-stack-tight items-end">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-stack w-full">
        {slots.map((slot, key) => {
          const isSelected = selectedSlotId === slot.id
          return (
            <button
              key={key}
              type="button"
              onClick={() => !isSelected && onSlotSelect(slot)}
              className={cn(
                'border border-border p-stack rounded-card-sm text-body-sm flex flex-col items-center justify-center text-fg-subtle',
                'hover:bg-bg-subtle/50 cursor-pointer',
                {
                  'border-brand bg-brand-subtle hover:bg-brand-subtle': isSelected,
                },
              )}
            >
              <div className="flex gap-stack-dense">
                <Text type="s">
                  {dayjs.utc(slot.start_at).format('HH:mm A')}
                </Text>
                –
                <Text type="s">
                  {dayjs.utc(slot.finish_at).format('HH:mm A')}
                </Text>
              </div>
              <Text type="xs">
                {slot.groomer.first_name} {slot.groomer.last_name}
              </Text>
            </button>
          )
        })}
      </div>

      {hasNextPage && !isFetchingNextPage && (
        <Button
          variant="ghost"
          type="button"
          className="text-fg-muted text-body-sm w-fit mx-auto mt-stack-tight"
          onClick={() => fetchNextPage()}
        >
          Load more
        </Button>
      )}

      {isFetchingNextPage && (
        <div className="flex gap-stack">
          {Array.from({ length: 3 }).map((_, key) => (
            <Skeleton
              key={key}
              height={64}
              width={189.33}
              className="rounded-surface w-full"
            />
          ))}
        </div>
      )}
    </div>
  )
}
